import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Spinner, Alert, Badge, Pagination, Button } from 'react-bootstrap';
import useAuthHeaders from '../../hooks/useAuthHeaders';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:5001/api/v1';
const PAGE_SIZE = 25;

// La API entrega la fecha en UTC (ocurridoEnUtc, sin zona en el JSON); se muestra en hora de México,
// igual que la pantalla de Bitácora del cliente, sin depender de la zona del navegador.
const formatDateTime = (utcStr) => {
  if (!utcStr) return '—';
  const iso = /(Z|[+-]\d{2}:\d{2})$/.test(utcStr) ? utcStr : `${utcStr}Z`;
  return new Date(iso).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

const formatDuration = (ms) => {
  if (ms == null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
};

const AdminUserDetail = () => {
  const { userUuid } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuthHeaders();

  const [result, setResult] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const fetchActivity = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = new URLSearchParams({ page: p, pageSize: PAGE_SIZE });
      const response = await fetch(
        `${API_BASE}/admin/users/${userUuid}/activity?${params}`,
        { headers }
      );
      if (!response.ok) throw new Error(`Error ${response.status}`);
      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, userUuid]);

  useEffect(() => {
    fetchActivity(page);
  }, [page, fetchActivity]);

  const totalPages = result.totalPages ?? 0;
  const currentPage = result.page ?? 1;

  const buildPageItems = () => {
    const items = [];
    const delta = 2;
    const left = Math.max(1, currentPage - delta);
    const right = Math.min(totalPages, currentPage + delta);
    if (left > 1) { items.push(1); if (left > 2) items.push('...'); }
    for (let i = left; i <= right; i++) items.push(i);
    if (right < totalPages) { if (right < totalPages - 1) items.push('...'); items.push(totalPages); }
    return items;
  };

  return (
    <div className="status-container">
      <div className="page-header mb-4 d-flex align-items-center gap-3">
        <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin')}>
          ← Volver
        </Button>
        <div>
          <h2 className="page-title mb-0">Actividad del usuario</h2>
          <p className="page-subtitle text-muted mb-0" style={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
            {userUuid} &mdash; {result.totalCount} registro{result.totalCount !== 1 ? 's' : ''} en los últimos 180 días
          </p>
          <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
            Fuente: bitácora de operaciones (consultas a SAT, IMSS, Infonavit, REPSE y descargas batch). EFOS no aparece.
          </p>
        </div>
      </div>

      {error && <Alert variant="danger">Error al cargar actividad: {error}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" style={{ color: '#13a0ff' }} />
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="mb-2">
              <thead className="table-light">
                <tr>
                  <th>Fecha (hora de México)</th>
                  <th>Operación</th>
                  <th>RFC</th>
                  <th>Resultado</th>
                  <th>Status</th>
                  <th>Razón del error</th>
                  <th className="text-end">Duración</th>
                  <th>Canal</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((row) => (
                  <tr key={row.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.ocurridoEnUtc)}</td>
                    <td title={[row.operation, row.requestId && `RequestId: ${row.requestId}`, row.operadorEmail && `Operador: ${row.operadorEmail}`].filter(Boolean).join('\n')}>
                      <code>{row.endpoint || row.operation || '—'}</code>
                    </td>
                    <td><code>{row.rfc ?? '—'}</code></td>
                    <td>
                      {row.resultado === 'Ok' || row.resultado === 'Error'
                        ? <Badge bg={row.resultado === 'Ok' ? 'success' : 'danger'}>{row.resultado}</Badge>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>{row.statusCode ?? '—'}</td>
                    <td style={{ maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {row.razonError ?? '—'}
                    </td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>{formatDuration(row.durationMs)}</td>
                    <td>{row.canal ?? '—'}</td>
                  </tr>
                ))}
                {result.items.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center text-muted py-4">
                      Este usuario no tiene actividad registrada.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-2">
              <small className="text-muted">
                Página {currentPage} de {totalPages}
              </small>
              <Pagination size="sm" className="mb-0">
                <Pagination.Prev disabled={currentPage === 1} onClick={() => setPage(p => p - 1)} />
                {buildPageItems().map((item, i) =>
                  item === '...'
                    ? <Pagination.Ellipsis key={`e${i}`} disabled />
                    : <Pagination.Item key={item} active={item === currentPage} onClick={() => setPage(item)}>{item}</Pagination.Item>
                )}
                <Pagination.Next disabled={currentPage === totalPages} onClick={() => setPage(p => p + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminUserDetail;
